import { Extension, type Editor, type Range } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion from '@tiptap/suggestion';
import { createSuggestionMenu, type SuggestionMenuItem } from './suggestionMenu';

interface SlashCommandContext {
   editor: Editor;
   range: Range;
}

export interface SceneSlashCommand extends SuggestionMenuItem {
   searchTerms: string[];
   run: (context: SlashCommandContext) => void;
}

const replaceSlashWith = (editor: Editor, range: Range, apply: (editor: Editor) => void) => {
   editor.chain().focus().deleteRange(range).run();
   apply(editor);
};

// Add future scene commands here. The suggestion extension and menu do not
// need to change when this registry grows.
export const SCENE_SLASH_COMMANDS: SceneSlashCommand[] = [
   {
      id: 'paragraph',
      title: 'Paragraph',
      subtitle: 'Plain body text',
      icon: '¶',
      searchTerms: ['text', 'normal'],
      run: ({ editor, range }) =>
         replaceSlashWith(editor, range, (instance) =>
            instance.chain().focus().setParagraph().run(),
         ),
   },
   {
      id: 'heading-1',
      title: 'Heading 1',
      subtitle: 'Large section heading',
      icon: 'H1',
      searchTerms: ['title', 'headline'],
      run: ({ editor, range }) =>
         replaceSlashWith(editor, range, (instance) =>
            instance.chain().focus().setHeading({ level: 1 }).run(),
         ),
   },
   {
      id: 'heading-2',
      title: 'Heading 2',
      subtitle: 'Medium section heading',
      icon: 'H2',
      searchTerms: ['subtitle', 'headline'],
      run: ({ editor, range }) =>
         replaceSlashWith(editor, range, (instance) =>
            instance.chain().focus().setHeading({ level: 2 }).run(),
         ),
   },
   {
      id: 'heading-3',
      title: 'Heading 3',
      subtitle: 'Small section heading',
      icon: 'H3',
      searchTerms: ['headline'],
      run: ({ editor, range }) =>
         replaceSlashWith(editor, range, (instance) =>
            instance.chain().focus().setHeading({ level: 3 }).run(),
         ),
   },
   {
      id: 'bullet-list',
      title: 'Bullet list',
      subtitle: 'Create an unordered list',
      icon: '•',
      searchTerms: ['unordered', 'list'],
      run: ({ editor, range }) =>
         replaceSlashWith(editor, range, (instance) =>
            instance.chain().focus().toggleBulletList().run(),
         ),
   },
   {
      id: 'ordered-list',
      title: 'Numbered list',
      subtitle: 'Create an ordered list',
      icon: '1.',
      searchTerms: ['ordered', 'list'],
      run: ({ editor, range }) =>
         replaceSlashWith(editor, range, (instance) =>
            instance.chain().focus().toggleOrderedList().run(),
         ),
   },
   {
      id: 'blockquote',
      title: 'Blockquote',
      subtitle: 'Emphasize a quoted passage',
      icon: '❝',
      searchTerms: ['quote', 'citation'],
      run: ({ editor, range }) =>
         replaceSlashWith(editor, range, (instance) =>
            instance.chain().focus().toggleBlockquote().run(),
         ),
   },
   {
      id: 'code-block',
      title: 'Code block',
      subtitle: 'Insert preformatted text',
      icon: '</>',
      searchTerms: ['code', 'preformatted'],
      run: ({ editor, range }) =>
         replaceSlashWith(editor, range, (instance) =>
            instance.chain().focus().toggleCodeBlock().run(),
         ),
   },
   {
      id: 'horizontal-rule',
      title: 'Divider',
      subtitle: 'Separate sections',
      icon: '—',
      searchTerms: ['rule', 'separator'],
      run: ({ editor, range }) =>
         replaceSlashWith(editor, range, (instance) =>
            instance.chain().focus().setHorizontalRule().run(),
         ),
   },
];

const slashCommandPluginKey = new PluginKey('sceneSlashCommands');

export const SlashCommands = Extension.create({
   name: 'sceneSlashCommands',

   addProseMirrorPlugins() {
      return [
         Suggestion<SceneSlashCommand, SceneSlashCommand>({
            editor: this.editor,
            pluginKey: slashCommandPluginKey,
            char: '/',
            startOfLine: true,
            items: ({ query }) => {
               const normalizedQuery = query.trim().toLowerCase();
               if (!normalizedQuery) return SCENE_SLASH_COMMANDS;

               return SCENE_SLASH_COMMANDS.filter((item) =>
                  [item.title, item.subtitle || '', ...item.searchTerms]
                     .join(' ')
                     .toLowerCase()
                     .includes(normalizedQuery),
               );
            },
            command: ({ editor, range, props }) => {
               props.run({ editor, range });
            },
            render: createSuggestionMenu<SceneSlashCommand>,
         }),
      ];
   },
});
